import * as R from 'ramda';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import {
  ArrayParam,
  DelimitedNumericArrayParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';

import { routes } from '@/config';
import { useSerpsMatch } from '@/domain/serps/hooks';
import { serpsSearchQuery } from '@/domain/serps/utils';
import { JobSearchQueryInput } from '@/graphql/generated';
import { getKey, setKey } from '@/helpers/storage';
import { useLoginStates } from '@/hooks';
import { SearchAlertQuery } from '@/types';

import { isFullRemoteOnlyByQueryParam } from '../helpers';
import { useGlobalSearch } from '../hooks/use-global-search';

type LocalStorageValue = {
  count: number;
  query: SearchAlertQuery;
};

type Params = {
  keywords?: string | null;
  location?: string | null;
  cityId?: string | null;
  radius?: number | null;
  company?: Array<string | null> | null;
  employmentType?: Array<string | null> | null;
  careerLevel?: Array<string | null> | null;
  discipline?: Array<string | null> | null;
  salary?: Array<number | null> | null;
  industry?: Array<string | null> | null;
  benefit?: Array<string | null> | null;
  country?: Array<string | null> | null;
  city?: Array<string | null> | null;
  sort?: string | null;
  page: number;
  remoteOption?: Array<string | null> | null;
  sc_o?: string | null;
};

export type SetQueryParamsInput = Omit<Params, 'page'> & { page?: number };

type QueryParams = [Params, (params?: SetQueryParamsInput) => void];

type SearchBoxInputs = {
  keywordsInput?: string;
  locationInput?: string;
  cityIdInput?: string;
  radiusInput?: number;
};

type SearchBox = [SearchBoxInputs, (params: SearchBoxInputs) => void];

type SearchQueryData = {
  searchQuery: JobSearchQueryInput;
  alertQuery: SearchAlertQuery;
  queryCount: number;
  filterCount: number;
  page: number;
  sort?: string;
  isUserCityLoading: boolean;
  searchBox: SearchBox;
  queryParams: QueryParams;
};

const LOCAL_STORE_KEY = 'searches';

const paramsShape = {
  company: ArrayParam,
  keywords: StringParam,
  location: StringParam,
  cityId: StringParam,
  radius: NumberParam,
  employmentType: ArrayParam,
  careerLevel: ArrayParam,
  discipline: ArrayParam,
  salary: DelimitedNumericArrayParam,
  industry: ArrayParam,
  benefit: ArrayParam,
  country: ArrayParam,
  city: ArrayParam,
  remoteOption: ArrayParam,
  sort: StringParam,
  page: withDefault(NumberParam, 1),
  searchAlertCount: NumberParam,
  sc_o: StringParam,
};

const unscrambleFilterId = (id: string): string => id.split('.')[0];

const unscrambleFilterIds = (ids: (string | null)[]): string[] =>
  ids.reduce((acc, id) => (id ? [...acc, unscrambleFilterId(id)] : acc), []);

const SearchQueryContext = React.createContext<SearchQueryData>({
  searchQuery: {},
  alertQuery: {},
  queryCount: 0,
  filterCount: 0,
  page: 1,
  isUserCityLoading: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  searchBox: [{}, () => {}],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  queryParams: [{ page: 1 }, () => {}],
});

export const SearchQueryProvider: React.FC = ({ children }) => {
  const { isUserCityLoading } = useGlobalSearch();
  const serps = useSerpsMatch();
  const history = useHistory();
  const { isLoggedOut } = useLoginStates();

  const [
    {
      company,
      keywords,
      location,
      cityId,
      radius,
      employmentType,
      careerLevel,
      discipline,
      salary,
      industry,
      benefit,
      country,
      city,
      remoteOption,
      sort,
      page,
    },
    setParams,
  ] = useQueryParams(paramsShape);

  const [{ keywordsInput, locationInput, cityIdInput, radiusInput }, setSearchBoxInputValues] = useState({
    keywordsInput: keywords ?? '',
    locationInput: location ?? '',
    cityIdInput: cityId ?? '',
    radiusInput: radius ?? 0,
  });

  const isSerps = !!serps;
  const isFullRemoteOnly = isFullRemoteOnlyByQueryParam(remoteOption);

  const redirectToSearch = useCallback(() => {
    history.replace(`${routes.search}${history.location.search}`);
  }, [history]);

  useEffect(() => {
    if (isSerps && !isLoggedOut) {
      redirectToSearch();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSearchBoxInputValuesWithPrevState = useCallback((params: SearchBoxInputs) => {
    setSearchBoxInputValues((prevState) => ({ ...prevState, ...params }));
  }, []);

  useEffect(() => {
    setSearchBoxInputValuesWithPrevState({ keywordsInput: keywords ?? '' });
  }, [keywords, setSearchBoxInputValuesWithPrevState]);

  useEffect(() => {
    setSearchBoxInputValuesWithPrevState({ locationInput: location ?? '' });
  }, [location, setSearchBoxInputValuesWithPrevState]);

  useEffect(() => {
    setSearchBoxInputValuesWithPrevState({ cityIdInput: cityId ?? '' });
  }, [cityId, setSearchBoxInputValuesWithPrevState]);

  useEffect(() => {
    setSearchBoxInputValuesWithPrevState({ radiusInput: radius ?? 0 });
  }, [radius, setSearchBoxInputValuesWithPrevState]);

  const filterCount =
    (employmentType?.length ?? 0) +
    (careerLevel?.length ?? 0) +
    (discipline?.length ?? 0) +
    (industry?.length ?? 0) +
    (benefit?.length ?? 0) +
    (country?.length ?? 0) +
    (city?.length ?? 0) +
    (salary?.length === 2 ? 1 : 0) +
    (remoteOption?.length ?? 0);

  const searchQuery = useMemo(() => {
    if (serps) {
      return serpsSearchQuery(serps);
    }

    const locationFields = {
      ...(cityId && { cityId }),
      ...(location && { text: location }),
      ...(radius && { radius }),
    };

    // We sort the arrays to normalise the query.
    // Having a different filter order won’t prevent hitting the Apollo cache
    const filter = {
      ...(company && { company: { id: [...company].sort() } }),
      ...(employmentType && { employmentType: { id: [...employmentType].sort() } }),
      ...(careerLevel && { careerLevel: { id: [...careerLevel].sort() } }),
      ...(discipline && { discipline: { id: [...discipline].sort() } }),
      ...(salary && { salary: { min: salary[0], max: salary[1] } }),
      ...(industry && { industry: { id: [...industry].sort() } }),
      ...(benefit && { benefit: { id: [...benefit].sort() } }),
      ...(country && { country: { id: [...country].sort() } }),
      ...(city && { city: { id: [...city].sort() } }),
      ...(remoteOption && { remoteOption: { id: [...remoteOption].sort() } }),
    };

    return {
      ...(keywords && { keywords }),
      ...(Object.keys(locationFields).length > 0 && { location: locationFields }),
      ...(Object.keys(filter).length > 0 && { filter }),
    };
  }, [
    benefit,
    careerLevel,
    city,
    cityId,
    company,
    country,
    discipline,
    employmentType,
    industry,
    keywords,
    location,
    radius,
    remoteOption,
    salary,
    serps,
  ]);

  // We set the URL query params in case of a SERPs page, in case the user wants
  // to continue searching
  useEffect(() => {
    if (isSerps && !keywords && !location && !employmentType && !discipline && !radius) {
      setParams(
        {
          keywords: searchQuery.keywords,
          location: searchQuery.location?.text,
          radius: searchQuery.location?.radius,
          employmentType: searchQuery.filter?.employmentType?.id,
          discipline: searchQuery.filter?.discipline?.id,
        },
        'replace'
      );
    }
  }, [
    discipline,
    employmentType,
    isSerps,
    keywords,
    location,
    searchQuery.filter?.discipline?.id,
    searchQuery.filter?.employmentType?.id,
    searchQuery.keywords,
    searchQuery.location?.text,
    searchQuery.location?.radius,
    setParams,
    radius,
  ]);

  const { alertQuery, queryCount } = useMemo(() => {
    const alertQuerySalary = salary?.reduce((acc, value) => (value ? [...acc, String(value)] : acc), []);

    const alertQueryFilters = {
      ...(company && { company: unscrambleFilterIds(company) }),
      ...(employmentType && { type: unscrambleFilterIds(employmentType) }),
      ...(benefit && { benefits: unscrambleFilterIds(benefit) }),
      ...(city && { city: unscrambleFilterIds(city) }),
      ...(country && { country: unscrambleFilterIds(country) }),
      ...(discipline && { discipline: unscrambleFilterIds(discipline) }),
      ...(industry && { industry: unscrambleFilterIds(industry) }),
      ...(careerLevel && { level: unscrambleFilterIds(careerLevel) }),
      ...(salary && { salary: alertQuerySalary }),
      ...(remoteOption && { remote_option: unscrambleFilterIds(remoteOption) }),
    };

    const alertQuery = {
      ...(keywords && { keywords }),
      ...(!isFullRemoteOnly && location && { location }),
      ...(!isFullRemoteOnly && radius && { radius: String(radius) }),
      ...(Object.keys(alertQueryFilters).length > 0 && { filter: alertQueryFilters }),
    };

    let queryCount = 1;
    let searches = getKey<LocalStorageValue[]>(LOCAL_STORE_KEY, []);
    const index = searches.findIndex(R.propEq(alertQuery, 'query'));

    if (index >= 0) {
      searches = R.adjust(index, R.evolve({ count: R.add(1) }), searches);
      queryCount = searches[index].count;
    } else {
      searches = R.append({ query: alertQuery, count: 1 }, searches);
    }

    setKey(LOCAL_STORE_KEY, searches);

    return {
      alertQuery,
      queryCount,
    };
  }, [
    benefit,
    careerLevel,
    city,
    company,
    country,
    discipline,
    employmentType,
    industry,
    isFullRemoteOnly,
    keywords,
    location,
    radius,
    remoteOption,
    salary,
  ]);

  const [queryParams, setQueryParams] = useMemo(() => {
    const queryParams = {
      company,
      keywords,
      location,
      cityId,
      radius,
      employmentType,
      careerLevel,
      discipline,
      salary,
      industry,
      benefit,
      country,
      city,
      remoteOption,
      sort,
      page,
    };

    const setQueryParams = (params?: SetQueryParamsInput): void => {
      const cleanedRadius = locationInput ? radiusInput : 0;
      setSearchBoxInputValues((prevState) => ({ ...prevState, radiusInput: cleanedRadius }));
      setParams({
        keywords: keywordsInput?.trim() || undefined,
        location: locationInput?.trim() || undefined,
        cityId: cityIdInput || undefined,
        radius: cleanedRadius || undefined,
        page: undefined,
        searchAlertCount: undefined,
        ...params,
      });

      // In case we are in a SERPs page, after any search criteria is changed,
      // we replace the url to /jobs/search so the user can keep searching
      if (isSerps) {
        redirectToSearch();
      }
    };

    return [queryParams, setQueryParams];
  }, [
    benefit,
    careerLevel,
    city,
    cityId,
    cityIdInput,
    company,
    country,
    discipline,
    employmentType,
    industry,
    isSerps,
    keywords,
    keywordsInput,
    location,
    locationInput,
    page,
    radius,
    radiusInput,
    redirectToSearch,
    remoteOption,
    salary,
    setParams,
    sort,
  ]);

  const contextData: SearchQueryData = {
    searchQuery,
    alertQuery,
    queryCount,
    filterCount,
    page,
    ...(sort && { sort }),
    isUserCityLoading,
    searchBox: [{ keywordsInput, locationInput, radiusInput }, setSearchBoxInputValuesWithPrevState],
    queryParams: [queryParams, setQueryParams],
  };

  return <SearchQueryContext.Provider value={contextData}>{children}</SearchQueryContext.Provider>;
};

export function useSearchQuery(): SearchQueryData {
  return useContext(SearchQueryContext);
}
